import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import PropTypes from 'prop-types';
import { Column } from 'primereact/column';
import { Dialog } from 'primereact/dialog';

import ButtonSp from '../../components/ButtonSp';
import InputTextSp from '../../components/InputTextSp';
import LabelSp from '../../components/LabelSp';
import DropdownSp from '../../components/DropdownSp';
import DataTableSp from '../../components/DataTableSp';
import { showMessage } from '../../components/MessageDialog';

import AtualizacaoLiberacaoService from '../../services/AtualizacaoLiberacaoService';
import AuthService from '../../services/AuthService';
import { errorHandle } from '../../util/functions';
import { AtualizacaoLiberacaoModel } from '../../util/Models';

const PERFIS = [
  { label: '1 - Piloto', value: 1 },
  { label: '2 - Geral', value: 2 },
  { label: '3 - Conservador', value: 3 },
];

// Liberações da versão: publicar != liberar. Linhas são adicionadas ao longo do
// tempo (beta nos pilotos -> depois "expandir" com uma linha todos).
function AtualizacaoCrudLiberacao(props) {
  AtualizacaoCrudLiberacao.propTypes = {
    idVersao: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
    viewMode: PropTypes.bool,
  };

  const { idVersao, viewMode } = props;

  const [liberacoes, setLiberações] = useState([]);
  const [dialogVisible, setDialogVisible] = useState(false);
  const [liberacao, setLiberacao] = useState(new AtualizacaoLiberacaoModel());

  const handleBuscar = useCallback(async () => {
    try {
      const result = await AtualizacaoLiberacaoService.findAll({ idVersao, limit: 50, page: 0 });
      setLiberações(result.items);
    } catch (err) {
      errorHandle(err);
    }
  }, [idVersao]);

  useEffect(() => {
    handleBuscar();
  }, [handleBuscar]);

  async function salvarLiberacao() {
    try {
      await AtualizacaoLiberacaoService.insert({ ...liberacao, idVersao });
      toast.success('Liberacao liberado.');
      setDialogVisible(false);
      handleBuscar();
    } catch (err) {
      errorHandle(err);
    }
  }

  const alternarSuspenso = useCallback(
    async r => {
      try {
        await AtualizacaoLiberacaoService.update({ id: r.id, suspensa: !r.suspensa });
        toast.success(r.suspensa ? 'Liberação reativada.' : 'Liberação suspensa.');
        handleBuscar();
      } catch (err) {
        errorHandle(err);
      }
    },
    [handleBuscar]
  );

  const excluirLiberacao = useCallback(
    r => {
      showMessage('Confirmação', 'Excluir esta liberação?', async idx => {
        if (idx === 1) {
          try {
            await AtualizacaoLiberacaoService.delete(r.id);
            toast.success('Liberação excluída.');
            handleBuscar();
          } catch (err) {
            errorHandle(err);
          }
        }
      });
    },
    [handleBuscar]
  );

  function descricaoAlvo(r) {
    if (Number(r.tipoAlvo) === 0) {
      return 'Todos os clientes';
    }
    if (Number(r.tipoAlvo) === 1) {
      const p = PERFIS.find(x => x.value === Number(r.idAlvo));
      return `Perfil ${p ? p.label : r.idAlvo}`;
    }
    return `Cliente Id ${r.idAlvo}`;
  }

  return (
    <>
      <div className="p-col-12" style={{ marginTop: 10 }}>
        <b>Liberações (quem recebe esta versão)</b>
        {!viewMode ? (
          <ButtonSp
            className="p-button-success"
            icon="pi pi-plus"
            label="Liberar"
            style={{ marginLeft: 10 }}
            disabled={!AuthService.checkRoles('ATUALIZACAO_INSERIR')}
            onClick={() => {
              setLiberacao(new AtualizacaoLiberacaoModel());
              setDialogVisible(true);
            }}
          />
        ) : null}
      </div>

      <div className="p-col-12 p-fluid datatable-doc-demo">
        <DataTableSp value={liberacoes} responsive>
          <Column field="id" header="Id" className="grid-col-id" />
          <Column className="grid-col" header="Alvo" body={descricaoAlvo} />
          <Column
            field="obrigatoria"
            className="grid-col-curr grid-col-center"
            header="Obrigatória"
            body={r => (r.obrigatoria ? 'Sim' : 'Não')}
          />
          <Column
            field="suspensa"
            className="grid-col-curr grid-col-center"
            header="Situação"
            body={r => (r.suspensa ? 'SUSPENSA' : 'Ativa')}
          />
          <Column
            field="liberadoEm"
            className="grid-col"
            header="Liberado em"
            body={r => (r.liberadoEm ? new Date(r.liberadoEm).toLocaleString('pt-BR') : '')}
          />
          <Column
            className="gid-col-acoes-35"
            bodyStyle={{ textAlign: 'end' }}
            body={r =>
              !viewMode ? (
                <>
                  <ButtonSp
                    className="p-button-warning"
                    icon={r.suspensa ? 'pi pi-play' : 'pi pi-pause'}
                    title={r.suspensa ? 'Reativar' : 'Suspender'}
                    disabled={!AuthService.checkRoles('ATUALIZACAO_ALTERAR')}
                    onClick={() => alternarSuspenso(r)}
                  />
                  <ButtonSp
                    className="p-button-danger"
                    icon="pi pi-trash"
                    title="Excluir"
                    disabled={!AuthService.checkRoles('ATUALIZACAO_EXCLUIR')}
                    onClick={() => excluirLiberacao(r)}
                  />
                </>
              ) : null
            }
          />
        </DataTableSp>
      </div>

      <Dialog
        header="Liberar versão"
        visible={dialogVisible}
        style={{ width: '95%', maxWidth: 500 }}
        modal
        onHide={() => setDialogVisible(false)}
      >
        <div className="p-grid p-fluid">
          <div className="p-col-12">
            <LabelSp>Alvo</LabelSp>
            <DropdownSp
              options={[
                { label: 'Todos os clientes', value: 0 },
                { label: 'Perfil de atualização', value: 1 },
                { label: 'Cliente específico', value: 2 },
              ]}
              value={liberacao.tipoAlvo}
              onChange={e => setLiberacao({ ...liberacao, tipoAlvo: e.target.value, idAlvo: 0 })}
            />
          </div>

          {Number(liberacao.tipoAlvo) === 1 ? (
            <div className="p-col-12">
              <LabelSp>Perfil</LabelSp>
              <DropdownSp
                options={PERFIS}
                value={Number(liberacao.idAlvo) || 1}
                onChange={e => setLiberacao({ ...liberacao, idAlvo: e.target.value })}
              />
            </div>
          ) : null}

          {Number(liberacao.tipoAlvo) === 2 ? (
            <div className="p-col-12">
              <LabelSp>Id do Cliente</LabelSp>
              <InputTextSp
                type="tel"
                value={liberacao.idAlvo || ''}
                onChange={e => setLiberacao({ ...liberacao, idAlvo: e.target.value })}
              />
            </div>
          ) : null}

          <div className="p-col-12">
            <LabelSp>Obrigatória (bloqueia login até atualizar)</LabelSp>
            <DropdownSp
              options={[
                { label: 'Não', value: false },
                { label: 'Sim', value: true },
              ]}
              value={liberacao.obrigatoria}
              onChange={e => setLiberacao({ ...liberacao, obrigatoria: e.target.value })}
            />
          </div>

          <div className="p-col-12" style={{ textAlign: 'end' }}>
            <ButtonSp
              className="p-button-success"
              icon="pi pi-check"
              label="Liberar"
              disabled={Number(liberacao.tipoAlvo) !== 0 && !Number(liberacao.idAlvo)}
              onClick={salvarLiberacao}
            />
            <ButtonSp
              className="p-button-secondary"
              label="Cancelar"
              onClick={() => setDialogVisible(false)}
            />
          </div>
        </div>
      </Dialog>
    </>
  );
}

export default AtualizacaoCrudLiberacao;
